import { sharedFeedbackValue, isSharedRemoteFeedback, sharedFeedbackText, type SharedRemoteFeedback } from "./sharedFeedbackPresentation";
import { getI18n, useTranslation } from "react-i18next";
import { createElement, Fragment, type ReactNode } from "react";
import source from "./locales/en/settingsManagement";
import type { PromptOptions } from "../components/ui/Dialog";

export type SettingsKey = { [K in keyof typeof source]: K extends `${infer Base}_one` | `${infer Base}_other` ? Base : K }[keyof typeof source];
export type SettingsMessage = SharedRemoteFeedback | { key: SettingsKey; values?: Record<string, string | number> };
export function settingsText(key: SettingsKey, values?: Record<string, string | number>): string {
  const engine = getI18n();
  // Flat producer keys contain dots; keep a narrow typed boundary instead of
  // recursively expanding every catalog key through i18next's selector type.
  if (engine?.isInitialized) return (engine as unknown as { t: (key: SettingsKey, options: Record<string, unknown>) => string }).t(key, { ...values, ns: "settingsManagement", keySeparator: false });
  const messages: Record<string, string> = source;
  const template = messages[key] ?? messages[`${key}_${values?.count === 1 ? "one" : "other"}`] ?? key;
  return template.replace(/{{\s*(\w+)\s*}}/g, (_match, name: string) => String(values?.[name] ?? ""));
}
export function useSettingsLanguage(): string {
  const { i18n } = useTranslation("settingsManagement");
  return i18n.resolvedLanguage ?? i18n.language ?? "en";
}
export const settingsLocal = (key: SettingsKey, values?: Record<string, string | number>): SettingsMessage => ({ key, values });
export const settingsMessage = (value: string | SettingsMessage | null): string | null => typeof value === "object" && value !== null ? isSharedRemoteFeedback(value) ? sharedFeedbackText(value) : settingsText(value.key, value.values) : value;
export const settingsLocale = (): string => getI18n()?.resolvedLanguage ?? getI18n()?.language ?? "en";
export const settingsNumber = (value: number): string => new Intl.NumberFormat(settingsLocale()).format(value);
export const settingsDialog = (renderText: () => PromptOptions): PromptOptions => ({ ...renderText(), renderText });
/** Insert React content into a complete plain-text message without catalog markup. */
export function settingsSlot(key: SettingsKey, name: string, node: ReactNode, values?: Record<string, string | number>): ReactNode {
  return settingsText(key, { ...values, [name]: "\uFFFC" }).split("\uFFFC").map((part, index) => createElement(Fragment, { key: index }, index > 0 ? node : null, part));
}

const remoteMessages = {
  "settings.validation.general.instanceName.max": [],
  "settings.validation.general.instanceName.min": [],
  "settings.validation.general.instanceDescription.max": [],
  "settings.validation.general.webRoot.max": [],
  "settings.validation.general.adminEmail.email": [],
  "settings.validation.general.timezone.max": [],
  "settings.validation.general.timezone.min": [],
  "settings.validation.general.dateFormat.max": [],
  "settings.validation.general.dateFormat.min": [],
  "settings.validation.general.locale.max": [],
  "settings.validation.general.locale.min": [],
  "settings.validation.general.sessionTimeout.range": [],
  "settings.validation.general.sessionTimeout.regex": [],
  "settings.validation.general.cookieExpiration.range": [],
  "settings.validation.general.cookieExpiration.regex": [],
  "settings.validation.general.defaultPage.max": [],
  "settings.validation.general.adminDefaultPage.max": [],
  "settings.validation.general.avatarMaxSizeKb.range": [],
  "settings.validation.general.avatarMaxSizeKb.regex": [],
  "settings.validation.general.bannerMaxSizeKb.range": [],
  "settings.validation.general.bannerMaxSizeKb.regex": [],
  "settings.validation.general.maintenanceMessage.max": [],
  "settings.validation.general.authMessageMarkdown.max": [],
  "settings.validation.general.dashboard.diskMount.max": [],
  "settings.validation.general.dashboard.diskMount.min": [],
  "settings.validation.general.dashboard.networkInterface.max": [],
  "settings.validation.general.bandwidth.startDate.regex": [],
  "settings.validation.general.telemetry.redisUrl.max": [],
  "settings.validation.general.telemetry.redisHost.max": [],
  "settings.validation.general.telemetry.redisPort.range": [],
  "settings.validation.general.telemetry.redisPort.regex": [],
  "settings.validation.general.telemetry.redisPassword.max": [],
  "settings.validation.general.audit.categories.max": [],
  "settings.validation.general.audit.retentionDays.range": [],
  "settings.validation.general.audit.retentionDays.regex": [],
  "settings.validation.general.central.instanceId.max": [],
  "settings.validation.general.central.apiKey.max": [],
  "settings.validation.general.media.storageRootTemplate.range": [],
  "settings.validation.general.media.storageRootTemplate.max": [],
  "settings.validation.general.vpn.config.stalenessPolicyDays.range": [],
  "settings.validation.general.vpn.config.stalenessPolicyDays.regex": [],
  "settings.validation.general.vpn.config.stalenessWarnLeadDays.range": [],
  "settings.validation.general.vpn.config.stalenessWarnLeadDays.regex": [],
  "settings.validation.general.vpn.config.minHealthyPoolPerRegion.range": [],
  "settings.validation.general.vpn.config.minHealthyPoolPerRegion.regex": [],
  "settings.validation.general.wsdashboard.mirror.ttlSeconds.range": [],
  "settings.validation.general.wsdashboard.mirror.ttlSeconds.regex": [],
  "settings.validation.general.defaultTheme.choice": [],
  "settings.validation.session.userTimeout.range": [],
  "settings.validation.session.userTimeout.regex": [],
  "settings.validation.session.guestTimeout.range": [],
  "settings.validation.session.guestTimeout.regex": [],
  "settings.validation.session.cookieExpiration.range": [],
  "settings.validation.session.cookieExpiration.regex": [],
  "settings.validation.registration.defaultQuota.regex": [],
  "settings.validation.registration.defaultGroup.range": [],
  "settings.validation.registration.defaultGroup.regex": [],
  "settings.validation.registration.usernameMinChars.range": [],
  "settings.validation.registration.usernameMinChars.regex": [],
  "settings.validation.registration.usernameMaxChars.range": [],
  "settings.validation.registration.usernameMaxChars.regex": [],
  "settings.validation.registration.passwordMinChars.range": [],
  "settings.validation.registration.passwordMinChars.regex": [],
  "settings.validation.registration.passwordMaxChars.range": [],
  "settings.validation.registration.passwordMaxChars.regex": [],
  "settings.validation.registration.usernameRegex.range": [],
  "settings.validation.registration.usernameRegex.max": [],
  "settings.validation.registration.usernameMinChars.order": [],
  "settings.validation.registration.passwordMinChars.order": [],
  "settings.insufficientPermissions": [],
  "settings.general.fetchFailed": [],
  "settings.general.updateFailed": [],
  "settings.session.fetchFailed": [],
  "settings.session.updateFailed": [],
  "settings.registration.fetchFailed": [],
  "settings.registration.updateFailed": [],
  "settings.session.invalidateFailed": [],
  "settings.branding.fetchFailed": [],
  "settings.branding.invalidAssetType": [],
  "settings.branding.multipartRequired": [],
  "settings.branding.noFile": [],
  "settings.branding.uploadFailed": [],
  "settings.branding.resetFailed": [],
  "settings.branding.assetIdRequired": [],
  "settings.branding.assetNotFound": [],
  "settings.branding.assetFileMissing": [],
  "settings.branding.promoteFailed": [],
  "settings.general.diskMountUnavailable": [],
  "settings.general.networkInterfaceUnavailable": [],
  "settings.general.bandwidthDateInvalid": [],
  "settings.general.bandwidthDateFuture": [],
  "settings.branding.maxSize": [
    "maxMb"
  ],
  "settings.branding.allowedTypes": [
    "allowed"
  ],
  "settings.branding.invalidSvg": [],
  "settings.branding.undetectableType": [],
  "settings.branding.unsupportedType": [],
  "settings.branding.extensionMismatch": [],
  "settings.registration.groupMissing": [
    "groupId"
  ],
  "settings.registration.adminGroupForbidden": [],
  "settings.branding.fileTooLarge": [],
  "settings.branding.readUploadFailed": [],
  "settings.validation.required": [],
  "settings.validation.invalidType": [
    "expected"
  ],
  "settings.validation.invalidChoice": [
    "options"
  ],
  "settings.validation.invalidInput": [],
  "settings.demoBlocked": [],
  "settings.unauthorized": [],
  "settings.databaseError": [],
  "settings.vnstat.scopedInterfaceRequired": [],
  "settings.vnstat.interfaceUnavailable": [],
  "settings.adminRequired": [],
  "settings.vnstat.noInterface": [],
  "settings.authorizationFailed": [],
  "settings.invalidBody": [],
  "settings.vnstat.invalidInterface": [],
  "settings.vnstat.enumerateFailed": [],
  "settings.vnstat.requestFailed": [],
  "settings.vnstat.rebuildUnexpected": [],
  "settings.redis.notConfigured": [],
  "settings.redis.diagnosisUnavailable": [],
  "settings.redis.connected": [],
  "settings.redis.connectedNonstandard": [],
  "settings.redis.connectFailed": [],
  "settings.redis.establishFailed": [],
  "settings.redis.connectionFailed": [],
  "settings.vnstat.resetConfirmation": [],
  "settings.vnstat.interfaceRequired": [],
  "settings.vnstat.cliMissing": [],
  "settings.vnstat.parseFailed": [],
  "settings.vnstat.noInterfaces": [],
  "settings.vnstat.noTraffic": [],
  "settings.vnstat.interfaceNameRequired": [],
  "settings.vnstat.addTimeout": [],
  "settings.vnstat.qbMissing": [],
  "settings.vnstat.rebuildTimeout": [],
  "settings.vnstat.executionFailed": [],
  "settings.vnstat.addExit": [
    "code"
  ],
  "settings.vnstat.rebuildExit": [
    "code"
  ],
  "settings.vnstat.resetExit": [
    "code"
  ],
  "settings.redis.helperMissing": [
    "path"
  ],
  "settings.redis.privilegesRequired": [],
  "settings.redis.notInstalled": [],
  "settings.redis.ensureUnreadable": [],
  "settings.redis.provisioned": [],
  "settings.redis.healUnreadable": [],
  "settings.redis.running": [],
  "settings.redis.stopped": [],
  "settings.redis.ensureFailed": [],
  "settings.redis.provisionFailed": [],
  "settings.redis.healFailed": [],
  "settings.redis.issue.ipv6_unavailable": [],
  "settings.redis.fix.ipv6_unavailable": [],
  "settings.redis.issue.duplicate_bind": [],
  "settings.redis.fix.duplicate_bind": [],
  "settings.redis.issue.duplicate_port": [],
  "settings.redis.fix.duplicate_port": [],
  "settings.redis.issue.duplicate_daemonize": [],
  "settings.redis.fix.duplicate_daemonize": [],
  "settings.redis.issue.daemonize_conflict": [],
  "settings.redis.fix.daemonize_conflict": [],
  "settings.redis.healed.ipv6_removed": [],
  "settings.redis.healed.deduplicated_bind": [],
  "settings.redis.healed.deduplicated_port": [],
  "settings.redis.healed.deduplicated_daemonize": [],
  "settings.redis.healed.daemonize_fixed": [],
  "settings.redis.healed.service_unmasked": [],
  "settings.redis.healed.service_enabled": [],
  "settings.redis.healed.service_restarted": [],
  "settings.redis.healed.service_started": [],
  "settings.redis.error.capability_check_failed": [],
  "settings.redis.error.ensure_command_failed": [],
  "settings.redis.error.parse_error": [],
  "settings.redis.error.heal_command_failed": [],
  "settings.redis.error.service_restart_failed": [],
  "settings.redis.error.not_installed": [],
  "settings.redis.error.unmask_failed": [],
  "settings.redis.error.enable_failed": [],
  "settings.redis.error.start_failed": [],
  "settings.redis.healNotInstalled": [],
  "settings.redis.healConfigMissing": [],
  "settings.redis.healingComplete": [],
  "settings.redis.ensureComplete": [],
  "settings.redis.ensureNotInstalled": [],
  "settings.vnstat.queryTimeout": [],
  "settings.vnstat.queryExit": [
    "code"
  ],
  "settings.redis.unknownError": []
} as const;

/** Only source-owned codes with validated parameters may select authored copy. */
export function settingsResponse(presentation: unknown, raw: unknown, fallback: SettingsKey): string | SettingsMessage {
  const shared = sharedFeedbackValue(raw, presentation);
  if (shared) return shared;
  if (presentation && typeof presentation === "object" && "code" in presentation && typeof presentation.code === "string" && Object.prototype.hasOwnProperty.call(remoteMessages, presentation.code)) {
    const code = presentation.code as keyof typeof remoteMessages;
    const params = "params" in presentation && presentation.params && typeof presentation.params === "object" ? presentation.params as Record<string, unknown> : {};
    const values: Record<string, string | number> = {};
    let valid = true;
    for (const name of remoteMessages[code]) {
      const value = Object.prototype.hasOwnProperty.call(params, name) ? params[name] : undefined;
      if ((typeof value !== "string" && typeof value !== "number") || (typeof value === "number" && !Number.isFinite(value))) { valid = false; break; }
      if ((name === "groupId" || name === "maxMb") ? typeof value !== "number" : typeof value !== "string") { valid = false; break; }
      values[name] = value;
    }
    if (valid) return settingsLocal(code, values);
  }
  return typeof raw === "string" ? raw : settingsLocal(fallback);
}
export function settingsFieldText<T extends Partial<Record<string, string[]>>>(fields: T, presentations: Record<string, unknown>): T {
  return Object.fromEntries(Object.entries(fields).map(([field, messages]) => [field, messages?.map((message, index) => settingsMessage(settingsResponse(Array.isArray(presentations[field]) ? presentations[field][index] : undefined, message, "pleaseCheckTheFormForErrors"))!)])) as T;
}


const brandingLabels = {
  "collapsedLogo": "brandLabelcollapsedLogo",
  "fullLogo": "brandLabelfullLogo",
  "favicon": "brandLabelfavicon",
  "defaultAvatar": "brandLabeldefaultAvatar",
  "defaultBanner": "brandLabeldefaultBanner",
  "defaultBotAvatar": "brandLabeldefaultBotAvatar",
  "emailLogoFull": "brandLabelemailLogoFull",
  "emailLogoIcon": "brandLabelemailLogoIcon"
} as const;
const brandingDescriptions = {
  "collapsedLogo": "brandDescriptioncollapsedLogo",
  "fullLogo": "brandDescriptionfullLogo",
  "favicon": "brandDescriptionfavicon",
  "defaultAvatar": "brandDescriptiondefaultAvatar",
  "defaultBanner": "brandDescriptiondefaultBanner",
  "defaultBotAvatar": "brandDescriptiondefaultBotAvatar",
  "emailLogoFull": "brandDescriptionemailLogoFull",
  "emailLogoIcon": "brandDescriptionemailLogoIcon"
} as const;
export const brandingAssetLabel = (slot: keyof typeof brandingLabels): string => settingsText(brandingLabels[slot]);
export const brandingAssetDescription = (slot: keyof typeof brandingDescriptions): string => settingsText(brandingDescriptions[slot]);
